import React, { useEffect, useState } from 'react';
import {
  ArrowRight, Star, Sparkles, ChefHat, Sofa, BedDouble, Refrigerator,
  Home as HomeIcon,
} from 'lucide-react';
import { heroHighlights } from '../mock';

const iconMap = { ChefHat, Sofa, BedDouble, Refrigerator };

const Hero = () => {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(
      () => setActiveIdx((p) => (p + 1) % heroHighlights.length),
      3500,
    );
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" className="relative min-h-screen overflow-hidden warm-grad pt-20">
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#F58220]/15 blur-3xl float-slow" />
      <div className="absolute -bottom-40 -left-32 w-[480px] h-[480px] rounded-full bg-[#E9D6C1]/60 blur-3xl float-slow" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center min-h-[calc(100vh-5rem)] py-10 lg:py-14">
        {/* LEFT */}
        <div className="relative z-10">
          <div className="fade-up inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#E9D6C1]">
            <Sparkles className="w-3.5 h-3.5 text-[#F58220]" />
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[#4D4D4D]">
              Modern Living. Made Simple.
            </span>
          </div>

          <h1 className="fade-up h-display uppercase text-[52px] md:text-[76px] lg:text-[92px] text-[#1a1410]">
            Homes
            <br />
            Happier
            <span className="block text-[#F58220]">Everyday</span>
          </h1>

          <p className="fade-up mt-6 text-[15px] md:text-[16px] leading-[1.8] text-[#4D4D4D] max-w-[540px]">
            Everything for a better home — kitchens, appliances, living,
            bedrooms, utilities and more. Curated brands, honest advice and
            reliable service for the modern Indian family.
          </p>

          <div className="fade-up mt-9 flex flex-wrap gap-3">
            <a
              href="#collections"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#collections')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary px-7 py-3.5 rounded-full text-[11px] uppercase tracking-[0.24em] flex items-center gap-3"
            >
              Shop Appliances
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-outline px-7 py-3.5 rounded-full text-[11px] uppercase tracking-[0.24em]"
            >
              Our Story
            </a>
          </div>

          <div className="fade-up mt-12 grid grid-cols-3 gap-6 max-w-[540px]">
            {[
              { k: '25+', l: 'Years of Trust' },
              { k: '15+', l: 'Premium Brands' },
              { k: '4.9★', l: 'Google Rating' },
            ].map((s) => (
              <div key={s.l}>
                <div className="h-display text-[36px] md:text-[42px] text-[#1a1410]">{s.k}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4D4D4D] mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>

          <div className="fade-up mt-8 font-script text-[26px] text-[#F58220]">
            For a Better Tomorrow
          </div>
        </div>

        {/* RIGHT — illustrated feature panel (no photos) */}
        <div className="relative h-[480px] md:h-[600px] lg:h-[640px]">
          {/* Main hero card */}
          <div
            className="absolute inset-0 rounded-[28px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(77,77,77,0.35)] border border-[#E9D6C1]"
            style={{
              background:
                'linear-gradient(160deg, #1a1410 0%, #2a2622 55%, #4D4D4D 100%)',
            }}
          >
            {/* Dotted pattern */}
            <svg className="absolute inset-0 w-full h-full opacity-15" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="heroDots" width="24" height="24" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1.4" fill="#F58220" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#heroDots)" />
            </svg>

            {/* Concentric halos */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-[#F58220]/12" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] rounded-full border border-[#F58220]/18" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[260px] h-[260px] rounded-full border border-[#F58220]/28" />

            {/* Center highlight — rotating icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative w-40 h-40 rounded-3xl bg-[#F58220] flex items-center justify-center shadow-2xl">
                {heroHighlights.map((h, i) => {
                  const Icon = iconMap[h.icon] || HomeIcon;
                  return (
                    <div
                      key={h.id}
                      className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ${
                        i === activeIdx ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                      }`}
                    >
                      <Icon className="w-20 h-20 text-white" strokeWidth={1.3} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Category label */}
            <div className="absolute bottom-8 left-8 right-8 text-[#F7F4EA]">
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#F58220]">
                Now Showing
              </div>
              <div className="h-display uppercase text-[36px] md:text-[44px] mt-2 leading-tight">
                {heroHighlights[activeIdx].title}
              </div>
              <div className="mt-4 flex gap-2">
                {heroHighlights.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    className={`h-[3px] rounded-full transition-all duration-500 ${
                      i === activeIdx ? 'w-10 bg-[#F58220]' : 'w-5 bg-[#F7F4EA]/25'
                    }`}
                    aria-label={`show ${heroHighlights[i].title}`}
                  />
                ))}
              </div>
            </div>

            {/* Category tabs top */}
            <div className="absolute top-6 left-6 right-6 flex justify-between gap-2">
              {heroHighlights.map((h, i) => {
                const Icon = iconMap[h.icon] || HomeIcon;
                return (
                  <button
                    key={h.id}
                    onClick={() => setActiveIdx(i)}
                    className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-full text-[10px] uppercase tracking-[0.16em] font-bold transition-all duration-400 ${
                      i === activeIdx
                        ? 'bg-[#F58220] text-white'
                        : 'bg-[#F7F4EA]/10 text-[#F7F4EA]/70 hover:bg-[#F7F4EA]/20'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline truncate">{h.title}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rating badge */}
          <div className="absolute -bottom-6 -left-6 hidden md:block">
            <div className="bg-[#F7F4EA] rounded-2xl p-5 shadow-[0_20px_50px_-20px_rgba(77,77,77,0.3)] w-[210px] border border-[#E9D6C1]">
              <div className="h-display text-[28px] text-[#1a1410] leading-none">
                4.9<span className="text-[#F58220]">★</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4D4D4D] mt-2">
                Loved by 5,000+ homes
              </div>
              <div className="mt-3 flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-[#F58220] text-[#F58220]" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[#4D4D4D]">
        <div className="text-[10px] uppercase tracking-[0.3em] font-semibold">Scroll</div>
        <div className="w-[1px] h-10 bg-gradient-to-b from-[#F58220] to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
